import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Teilbare Rechner-Ergebnisse per Query-Params.
 * - getNumber/getString lesen Startwerte aus der URL
 * - share(values, text) baut den Link, nutzt Web Share (mobil) oder Clipboard
 */
export function useShareableCalc({ path, title = 'ivoliq', markerKey }) {
  const [searchParams] = useSearchParams();

  const getNumber = useCallback((key, fallback) => {
    if (!searchParams.has(key)) return fallback;
    const v = Number(searchParams.get(key));
    return Number.isFinite(v) ? v : fallback;
  }, [searchParams]);

  const getString = useCallback((key, fallback, allowed) => {
    const v = searchParams.get(key);
    if (v === null) return fallback;
    return allowed && !allowed.includes(v) ? fallback : v;
  }, [searchParams]);

  const isShared = searchParams.has(markerKey);

  const buildUrl = useCallback((values) => {
    const params = new URLSearchParams();
    Object.entries(values).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, String(v)); });
    return `${window.location.origin}${path}?${params.toString()}`;
  }, [path]);

  const share = useCallback(async (values, text) => {
    const url = buildUrl(values);
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link kopiert! Teile dein Ergebnis mit anderen Freelancern.');
    } catch {
      window.prompt('Link zum Teilen kopieren:', url);
    }
  }, [buildUrl, title]);

  return { getNumber, getString, isShared, buildUrl, share };
}

export default useShareableCalc;
